import { createSlice } from "@reduxjs/toolkit";

const adminUserSlice = createSlice({
  name: "adminUser",
  initialState: {
    status: "idle",
    error: null,
    loading: false,
    users: [],
    selectedUser: null,
    roles: [],
    filters: {
      user_type: "",
      client_id: "",
      is_active: "",
      search: "",
      limit: 50,
      skip: 0,
      sort_by: "created_at",
      sort_order: "desc"
    },
    pagination: {
      total: 0,
      limit: 50,
      skip: 0,
      totalPages: 0
    },
    clientUsers: [],
    selectedClientId: null
  },
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
      state.status = "failed";
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearError: (state) => {
      state.error = null;
      state.status = "idle";
    },
    loadUsers: (state, action) => {
      const usersData = action.payload.data || action.payload || [];
      
      // Normalize user ID field - ensure all users have user_id field
      const normalizedUsers = usersData.map(user => ({
        ...user,
        user_id: user.user_id || user.id || user._id || user.userId
      }));
      
      console.log('Normalized users:', normalizedUsers);
      
      state.users = normalizedUsers;
      state.pagination = {
        total: action.payload.total || 0,
        limit: action.payload.limit || 50,
        skip: action.payload.skip || 0,
        totalPages: action.payload.totalPages || 0
      };
      state.status = "succeeded";
    },
    addUser: (state, action) => {
      const user = {
        ...action.payload,
        user_id: action.payload.user_id || action.payload.id || action.payload._id || action.payload.userId
      };
      state.users.unshift(user);
    },
    updateUserInList: (state, action) => {
      const { user_id, updatedUser } = action.payload;
      const normalizedUserId = user_id || updatedUser.user_id || updatedUser.id || updatedUser._id || updatedUser.userId;
      state.users = state.users.map(user =>
        user.user_id === normalizedUserId ? { ...user, ...updatedUser } : user
      );
    },
    removeUserFromList: (state, action) => {
      const userId = action.payload;
      state.users = state.users.filter(user => user.user_id !== userId);
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    loadRoles: (state, action) => {
      // Handle roles data structure - it comes as an object with role keys
      const rolesData = action.payload.data || action.payload || {};
      // Convert object to array format for easier handling in components
      const rolesArray = Object.entries(rolesData).map(([roleKey, roleData]) => ({
        role: roleKey,
        ...roleData
      }));
      state.roles = rolesArray;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        user_type: "",
        client_id: "",
        is_active: "",
        search: "",
        limit: 50,
        skip: 0,
        sort_by: "created_at",
        sort_order: "desc"
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    loadClientUsers: (state, action) => {
      state.clientUsers = action.payload.users || action.payload.data || [];
      state.selectedClientId = action.payload.client_id;
    },
    addClientUser: (state, action) => {
      state.clientUsers.unshift(action.payload);
    },
    updateClientUserInList: (state, action) => {
      const { user_id, updatedUser } = action.payload;
      state.clientUsers = state.clientUsers.map(user =>
        user.user_id === user_id ? { ...user, ...updatedUser } : user
      );
    },
    removeClientUserFromList: (state, action) => {
      const userId = action.payload;
      state.clientUsers = state.clientUsers.filter(user => user.user_id !== userId);
    },
    clearClientUsers: (state) => {
      state.clientUsers = [];
      state.selectedClientId = null;
    },
    clearAdminUserState: (state) => {
      state.status = "idle";
      state.error = null;
      state.loading = false;
      state.users = [];
      state.selectedUser = null;
      state.roles = [];
      state.filters = {
        user_type: "",
        client_id: "",
        is_active: "",
        search: "",
        limit: 50,
        skip: 0,
        sort_by: "created_at",
        sort_order: "desc"
      };
      state.pagination = {
        total: 0,
        limit: 50,
        skip: 0,
        totalPages: 0
      };
      state.clientUsers = [];
      state.selectedClientId = null;
    }
  },
});

export const {
  setError,
  setLoading,
  clearError,
  loadUsers,
  addUser,
  updateUserInList,
  removeUserFromList,
  setSelectedUser,
  clearSelectedUser,
  loadRoles,
  setFilters,
  clearFilters,
  setPagination,
  loadClientUsers,
  addClientUser,
  updateClientUserInList,
  removeClientUserFromList,
  clearClientUsers,
  clearAdminUserState
} = adminUserSlice.actions;

// Selectors
export const selectAdminUserLoading = (state) => state.adminUser.loading;
export const selectAdminUserError = (state) => state.adminUser.error;
export const selectAdminUserStatus = (state) => state.adminUser.status;
export const selectAdminUsers = (state) => state.adminUser.users;
export const selectSelectedUser = (state) => state.adminUser.selectedUser;
export const selectAdminUserRoles = (state) => state.adminUser.roles;
export const selectAdminUserFilters = (state) => state.adminUser.filters;
export const selectAdminUserPagination = (state) => state.adminUser.pagination;
export const selectClientUsers = (state) => state.adminUser.clientUsers;
export const selectSelectedClientId = (state) => state.adminUser.selectedClientId;

export default adminUserSlice.reducer;
